import { useState, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api, { BudgetRequest as BRType, budgetRequestApi, DealAttachment, ADMIN_ROLES, ApprovalEntry } from '@/lib/api'
import { formatDateOnly, formatMyanmarDateTime } from '@/lib/dateUtils'
import SlideOver from '@/components/SlideOver'
import MultiProjectSelect from '@/components/MultiProjectSelect'
import type { ProjectInfo } from '@/components/MultiProjectSelect'
import ContactSearchSelect from '@/components/ContactSearchSelect'
import type { ContactInfo } from '@/components/ContactSearchSelect'
import CommentsSection from '@/components/CommentsSection'
import { useMyPermissions } from '@/hooks/useRolePermissions'
import ApprovalsPanel from '@/components/ApprovalsPanel'
import {
  Plus, Search, AlertCircle, CheckCircle, Clock, XCircle,
  FileText, Image, Upload, X, Wallet, ChevronDown, Trash2, FileDown
} from 'lucide-react'

const BUDGET_TYPES = [
  'Trip',
  'Sale Expense',
  'Equipment Purchase',
  'Labor',
  'Sub Contractor',
  'Marketing Expense',
  'Admin Expense',
  'Inventory Purchase',
]

const LINKED_TYPES = new Set([
  'Trip', 'Sale Expense', 'Equipment Purchase', 'Labor', 'Sub Contractor',
])

const FINANCE_ADMIN = ['super_admin', 'admin', 'finance']

function statusBadge(status: string) {
  if (status === 'Approved') return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium"><CheckCircle size={12} />{status}</span>
  if (status === 'Rejected') return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium"><XCircle size={12} />{status}</span>
  if (status === 'Disbursed') return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 font-medium"><CheckCircle size={12} />{status}</span>
  return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium"><Clock size={12} />{status}</span>
}

function formatAmount(v: number) {
  if (!v) return '0 MMK'
  if (v >= 100000) return `${(v / 100000).toFixed(v % 100000 === 0 ? 0 : 1)} Lakhs MMK`
  return `${v.toLocaleString()} MMK`
}

function formatDate(d: string | null) {
  if (!d) return '—'
  return formatDateOnly(d) || d
}

function formatDateTime(d: string | null) {
  if (!d) return '—'
  return formatMyanmarDateTime(d) || d
}

export default function BudgetRequest() {
  const queryClient = useQueryClient()
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
  const userRole = currentUser.role || ''
  const isFinanceAdmin = FINANCE_ADMIN.includes(userRole)
  const { canDo } = useMyPermissions()
  const canApprove = canDo('finance_approve')

  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState('')
  const [selectedBR, setSelectedBR] = useState<BRType | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [tab, setTab] = useState<'details' | 'attachments' | 'comments'>('details')
  const [form, setForm] = useState<any>({})
  const [formProjects, setFormProjects] = useState<ProjectInfo[]>([])
  const [formContact, setFormContact] = useState('')
  const [formError, setFormError] = useState('')
  const [formPhotos, setFormPhotos] = useState<File[]>([])
  const [formDocs, setFormDocs] = useState<File[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState('')
  const [rejectMode, setRejectMode] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [uploadingDoc, setUploadingDoc] = useState(false)
  const [photoError, setPhotoError] = useState('')
  const [docError, setDocError] = useState('')
  const photoRef = useRef<HTMLInputElement>(null)
  const docRef = useRef<HTMLInputElement>(null)
  const formPhotoRef = useRef<HTMLInputElement>(null)
  const formDocRef = useRef<HTMLInputElement>(null)

  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) {
      api.get(`/budget-requests/${openId}`).then(r => {
        setSelectedBR(r.data)
        setSearchParams({}, { replace: true })
      }).catch(() => setSearchParams({}, { replace: true }))
    }
  }, [])

  const { data: brs = [], isLoading } = useQuery<BRType[]>({
    queryKey: ['budget-requests', statusFilter],
    queryFn: () => budgetRequestApi.list({ status: statusFilter || undefined }).then(r => r.data),
  })

  const { data: brSummary } = useQuery({
    queryKey: ['budget-requests-summary'],
    queryFn: () => budgetRequestApi.summary().then(r => r.data),
    enabled: isFinanceAdmin,
  })

  const approveMut = useMutation({
    mutationFn: (id: number) => budgetRequestApi.approve(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['budget-requests'] })
      setSelectedBR(prev => prev ? { ...prev, status: res.data.status, approvals: res.data.approvals } : prev)
    },
  })

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => budgetRequestApi.reject(id, reason),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['budget-requests'] })
      setSelectedBR(prev => prev ? { ...prev, status: res.data.status, approvals: res.data.approvals } : prev)
      setRejectMode(false)
      setRejectReason('')
    },
  })

  const revokeMut = useMutation({
    mutationFn: (id: number) => budgetRequestApi.revokeApproval(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['budget-requests'] })
      setSelectedBR(prev => prev ? { ...prev, status: res.data.status, approvals: res.data.approvals } : prev)
    },
  })

  const deletePhotoMut = useMutation({
    mutationFn: ({ id, photoId }: { id: number; photoId: string }) => budgetRequestApi.deletePhoto(id, photoId),
    onSuccess: (_res, vars) => {
      setSelectedBR(prev => prev ? { ...prev, photos: (prev.photos || []).filter((p: any) => p.id !== vars.photoId) } : prev)
      queryClient.invalidateQueries({ queryKey: ['budget-requests'] })
    },
  })

  const deleteDocMut = useMutation({
    mutationFn: ({ id, docId }: { id: number; docId: string }) => budgetRequestApi.deleteDocument(id, docId),
    onSuccess: (_res, vars) => {
      setSelectedBR(prev => prev ? { ...prev, documents: (prev.documents || []).filter((d: any) => d.id !== vars.docId) } : prev)
      queryClient.invalidateQueries({ queryKey: ['budget-requests'] })
    },
  })

  const deleteBRMut = useMutation({
    mutationFn: (id: number) => budgetRequestApi.deleteBR(id),
    onSuccess: () => {
      setSelectedBR(null)
      queryClient.invalidateQueries({ queryKey: ['budget-requests'] })
    },
  })

  const [showDisburse, setShowDisburse] = useState(false)
  const [disburseRef, setDisburseRef] = useState('')

  const disburseMut = useMutation({
    mutationFn: ({ id, reference }: { id: number; reference?: string }) =>
      budgetRequestApi.disburse(id, reference),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['budget-requests'] })
      setSelectedBR(prev => prev ? { ...prev, status: res.data.status, disbursed_at: res.data.disbursed_at } : prev)
      setShowDisburse(false)
      setDisburseRef('')
    },
  })

  function resetForm() {
    setForm({})
    setFormProjects([])
    setFormContact('')
    setFormError('')
    setFormPhotos([])
    setFormDocs([])
    setSubmitStatus('')
    if (formPhotoRef.current) formPhotoRef.current.value = ''
    if (formDocRef.current) formDocRef.current.value = ''
  }

  function handleFormPhotoAdd(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || [])
    setFormPhotos(prev => [...prev, ...files].slice(0, 5))
    if (formPhotoRef.current) formPhotoRef.current.value = ''
  }

  function handleFormDocAdd(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || [])
    setFormDocs(prev => [...prev, ...files].slice(0, 5))
    if (formDocRef.current) formDocRef.current.value = ''
  }

  const filteredBRs = (brs || []).filter(b => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      b.br_number.toLowerCase().includes(q) ||
      b.title.toLowerCase().includes(q) ||
      b.budget_type.toLowerCase().includes(q) ||
      (b.submitted_by_name || '').toLowerCase().includes(q)
    )
  })

  async function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError('')
    if (!form.budget_type) { setFormError('Please select a budget type'); return }
    if (!form.title?.trim()) { setFormError('Please enter a title'); return }
    if (!form.amount || parseFloat(form.amount) <= 0) { setFormError('Please enter a valid amount'); return }
    if (LINKED_TYPES.has(form.budget_type) && formProjects.length === 0 && !form.client_id) {
      setFormError(`Budget type "${form.budget_type}" requires at least one linked project or a contact`)
      return
    }
    setIsSubmitting(true)
    setSubmitStatus(formPhotos.length > 0 || formDocs.length > 0 ? 'Submitting request with attachments...' : 'Submitting request...')
    try {
      await budgetRequestApi.create({
        budget_type: form.budget_type,
        title: form.title.trim(),
        amount: parseFloat(form.amount),
        project_id: formProjects.length > 0 ? formProjects[0].id : null,
        client_id: form.client_id || null,
        project_ids: formProjects.map(p => p.id),
        additional_approvers: form.additional_approvers || [],
        requested_date: form.requested_date || null,
        notes: form.notes?.trim() || null,
        photos: formPhotos,
        documents: formDocs,
      })
      queryClient.invalidateQueries({ queryKey: ['budget-requests'] })
      setShowCreate(false)
      resetForm()
    } catch (err: any) {
      const status = err.response?.status
      const detail = err.response?.data?.detail
      if (detail) {
        setFormError(detail)
      } else if (status === 403 || status === 401) {
        setFormError('Your session has expired. Please log out and log back in, then try again.')
      } else if (status === 503) {
        setFormError('File storage is temporarily unavailable. Remove any attachments and try again, or contact an administrator.')
      } else if (status === 502) {
        setFormError('Failed to upload attachment to storage. Please try again.')
      } else if (!err.response) {
        setFormError('No connection to server. Please check your internet connection and try again.')
      } else {
        setFormError(`Submission failed (server error ${status ?? 'unknown'}). Please try again or contact support.`)
      }
    } finally {
      setIsSubmitting(false)
      setSubmitStatus('')
    }
  }

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !selectedBR) return
    setPhotoError('')
    setUploadingPhoto(true)
    try {
      const res = await budgetRequestApi.uploadPhoto(selectedBR.id, file)
      setSelectedBR(prev => prev ? { ...prev, photos: [...(prev.photos || []), res.data.file] } : prev)
      queryClient.invalidateQueries({ queryKey: ['budget-requests'] })
    } catch (err: any) {
      setPhotoError(err.response?.data?.detail || 'Failed to upload photo')
    } finally {
      setUploadingPhoto(false)
      if (photoRef.current) photoRef.current.value = ''
    }
  }

  async function handleDocUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !selectedBR) return
    setDocError('')
    setUploadingDoc(true)
    try {
      const res = await budgetRequestApi.uploadDocument(selectedBR.id, file)
      setSelectedBR(prev => prev ? { ...prev, documents: [...(prev.documents || []), res.data.file] } : prev)
      queryClient.invalidateQueries({ queryKey: ['budget-requests'] })
    } catch (err: any) {
      setDocError(err.response?.data?.detail || 'Failed to upload document')
    } finally {
      setUploadingDoc(false)
      if (docRef.current) docRef.current.value = ''
    }
  }

  const canUpload = selectedBR
    ? (isFinanceAdmin || selectedBR.submitted_by === currentUser.id)
    : false

  const needsLink = LINKED_TYPES.has(form.budget_type || '')

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Wallet size={22} className="text-red-800" />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Budget Requests</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={async () => {
            try {
              const res = await api.get('/export/budget-requests', { params: { status: statusFilter, search }, responseType: 'blob' })
              const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = 'budget_requests.xlsx'; a.click(); URL.revokeObjectURL(url)
            } catch {}
          }} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600" title="Export to Excel">
            <FileDown size={15} /> <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={() => { resetForm(); setShowCreate(true) }}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900 min-h-[44px]"
          >
            <Plus size={16} /> New Request
          </button>
        </div>
      </div>

      {/* Finance Summary Strip */}
      {isFinanceAdmin && brSummary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Pending', count: brSummary.count_pending, total: brSummary.total_pending, color: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
            { label: 'Approved', count: brSummary.count_approved, total: brSummary.total_approved, color: 'bg-green-50 border-green-200 text-green-700' },
            { label: 'Disbursed', count: brSummary.count_disbursed, total: brSummary.total_disbursed, color: 'bg-teal-50 border-teal-200 text-teal-700' },
            { label: 'Rejected', count: brSummary.count_rejected, total: brSummary.total_rejected, color: 'bg-red-50 border-red-200 text-red-700' },
          ].map(s => (
            <div key={s.label} className={`rounded-xl border p-3 ${s.color}`}>
              <div className="text-xs font-medium opacity-80">{s.label}</div>
              <div className="text-lg font-bold mt-0.5">{s.count}</div>
              <div className="text-xs opacity-70">{formatAmount(s.total)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
          <input
            type="text"
            placeholder="Search by number, title, type..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
        >
          <option value="">All Statuses</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Disbursed">Disbursed</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-800" />
          </div>
        ) : filteredBRs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-3">
            <Wallet size={40} strokeWidth={1} />
            <p className="text-sm">No budget requests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">BR #</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">Title</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-600 text-xs uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">Submitted By</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredBRs.map(br => (
                  <tr
                    key={br.id}
                    onClick={() => { setSelectedBR(br); setTab('details'); setRejectMode(false); setPhotoError(''); setDocError('') }}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-red-900">{br.br_number}</td>
                    <td className="px-4 py-3 text-slate-600">{br.budget_type}</td>
                    <td className="px-4 py-3 font-medium text-slate-900 max-w-[180px] truncate">{br.title}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatAmount(Number(br.amount))}</td>
                    <td className="px-4 py-3 text-slate-600">{br.submitted_by_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-500">{formatDate(br.requested_date || br.created_at)}</td>
                    <td className="px-4 py-3">{statusBadge(br.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create SlideOver */}
      <SlideOver open={showCreate} onClose={() => { setShowCreate(false); resetForm() }} title="New Budget Request">
        <form onSubmit={handleFormSubmit} className="space-y-4">
          {formError && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200">
              <AlertCircle size={16} className="shrink-0" /><span>{formError}</span>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Budget Type *</label>
            <select
              value={form.budget_type || ''}
              onChange={e => { setForm({ ...form, budget_type: e.target.value, project_id: null, client_id: null }); setFormProjects([]); setFormContact('') }}
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
              required
            >
              <option value="">Select type...</option>
              {BUDGET_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {needsLink && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
              This budget type requires linking at least one Project Bank project <strong>or</strong> a contact.
            </div>
          )}

          {needsLink && (
            <MultiProjectSelect
              selected={formProjects}
              onAdd={(p: ProjectInfo) => {
                setFormProjects(prev => prev.find(x => x.id === p.id) ? prev : [...prev, p])
                setForm((f: any) => ({ ...f, client_id: null }))
                setFormContact('')
              }}
              onRemove={(id: number) => setFormProjects(prev => prev.filter(p => p.id !== id))}
            />
          )}

          {needsLink && formProjects.length === 0 && (
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Contact (if no project)</label>
              <ContactSearchSelect
                value={formContact}
                onSelect={(c: ContactInfo) => { setForm((f: any) => ({ ...f, client_id: c.id })); setFormContact(c.company ? `${c.client_name} — ${c.company}` : c.client_name) }}
                onClear={() => { setForm((f: any) => ({ ...f, client_id: null })); setFormContact('') }}
              />
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Title *</label>
            <input
              type="text"
              value={form.title || ''}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Brief description of the budget request"
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Amount (MMK) *</label>
            <input
              type="number"
              min="1"
              step="any"
              value={form.amount || ''}
              onChange={e => setForm({ ...form, amount: e.target.value })}
              placeholder="0"
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Requested Date</label>
            <input
              type="date"
              value={form.requested_date || ''}
              onChange={e => setForm({ ...form, requested_date: e.target.value })}
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Notes</label>
            <textarea
              value={form.notes || ''}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Additional details..."
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 resize-none"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-2">Additional Approvers</label>
            <div className="space-y-2">
              {['aungthanzaw', 'tuntunwin', 'thwinhtooaung'].map(username => {
                const selected: string[] = form.additional_approvers || []
                return (
                  <label key={username} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={selected.includes(username)}
                      onChange={e => {
                        const next = e.target.checked
                          ? [...selected, username]
                          : selected.filter(u => u !== username)
                        setForm({ ...form, additional_approvers: next })
                      }}
                      className="w-4 h-4 accent-red-800"
                    />
                    <span className="text-sm font-medium text-slate-800">{username}</span>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Form Photos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Photos (optional, max 5)</label>
              {formPhotos.length < 5 && (
                <label className="text-xs text-red-800 hover:text-red-900 font-medium flex items-center gap-1 cursor-pointer">
                  <Plus size={12} /> Add Photo
                  <input ref={formPhotoRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFormPhotoAdd} />
                </label>
              )}
            </div>
            {formPhotos.length > 0 && (
              <div className="space-y-1">
                {formPhotos.map((f, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200 text-sm">
                    <Image size={12} className="text-slate-400 shrink-0" />
                    <span className="flex-1 truncate text-slate-700 text-xs">{f.name}</span>
                    <button type="button" onClick={() => setFormPhotos(prev => prev.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-600 shrink-0">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form Documents */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Documents (optional, max 5)</label>
              {formDocs.length < 5 && (
                <label className="text-xs text-red-800 hover:text-red-900 font-medium flex items-center gap-1 cursor-pointer">
                  <FileText size={12} /> Add Document
                  <input ref={formDocRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*" multiple className="hidden" onChange={handleFormDocAdd} />
                </label>
              )}
            </div>
            {formDocs.length > 0 && (
              <div className="space-y-1">
                {formDocs.map((f, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200 text-sm">
                    <FileText size={12} className="text-slate-400 shrink-0" />
                    <span className="flex-1 truncate text-slate-700 text-xs">{f.name}</span>
                    <button type="button" onClick={() => setFormDocs(prev => prev.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-600 shrink-0">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {submitStatus && (
            <div className="flex items-center gap-2 bg-blue-50 text-blue-700 text-sm px-4 py-3 rounded-lg border border-blue-200">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-700 shrink-0" />
              <span>{submitStatus}</span>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={isSubmitting}
              className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
            <button type="button" onClick={() => { setShowCreate(false); resetForm() }} disabled={isSubmitting}
              className="py-2 px-4 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50">
              Cancel
            </button>
          </div>
        </form>
      </SlideOver>

      {/* Detail SlideOver */}
      <SlideOver
        open={!!selectedBR}
        onClose={() => { setSelectedBR(null); setRejectMode(false); setPhotoError(''); setDocError('') }}
        title={selectedBR ? `${selectedBR.br_number} — ${selectedBR.title}` : ''}
        wide
      >
        {selectedBR && (
          <div className="space-y-4">
            {/* Status banner */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              {statusBadge(selectedBR.status)}
              <div className="flex items-center gap-2">
                {selectedBR.status === 'Approved' && canApprove && !showDisburse && (
                  <button
                    onClick={() => setShowDisburse(true)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-teal-700 border border-teal-300 rounded-lg hover:bg-teal-50"
                  >
                    <CheckCircle size={13} /> Mark as Disbursed
                  </button>
                )}
                {selectedBR.status === 'Pending' && (canApprove || selectedBR.submitted_by === currentUser.id) && (
                  <button
                    onClick={() => { if (confirm('Delete this budget request? This cannot be undone.')) deleteBRMut.mutate(selectedBR.id) }}
                    disabled={deleteBRMut.isPending}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash2 size={13} /> {deleteBRMut.isPending ? 'Deleting…' : 'Delete'}
                  </button>
                )}
              </div>
            </div>

            {/* Disburse inline form */}
            {showDisburse && selectedBR.status === 'Approved' && canApprove && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-teal-800">Mark as Disbursed</p>
                <input
                  type="text"
                  value={disburseRef}
                  onChange={e => setDisburseRef(e.target.value)}
                  placeholder="Reference / Voucher number (optional)"
                  className="w-full px-3 py-2 text-sm border border-teal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => disburseMut.mutate({ id: selectedBR.id, reference: disburseRef || undefined })}
                    disabled={disburseMut.isPending}
                    className="flex-1 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                  >
                    {disburseMut.isPending ? 'Disbursing…' : 'Confirm Disbursement'}
                  </button>
                  <button
                    onClick={() => { setShowDisburse(false); setDisburseRef('') }}
                    className="py-2 px-4 text-sm bg-white border border-teal-200 text-teal-700 rounded-lg hover:bg-teal-50"
                  >
                    Cancel
                  </button>
                </div>
                {disburseMut.isError && (
                  <p className="text-xs text-red-600">{(disburseMut.error as any)?.response?.data?.detail || 'Failed to disburse'}</p>
                )}
              </div>
            )}

            {/* Approvals Panel */}
            <ApprovalsPanel
              approvals={selectedBR.approvals || []}
              additionalApprovers={selectedBR.additional_approvers || []}
              currentUsername={currentUser.username || ''}
              currentRole={userRole}
              isPending={selectedBR.status === 'Pending'}
              rejectMode={rejectMode}
              rejectReason={rejectReason}
              onRejectReasonChange={setRejectReason}
              onApprove={() => approveMut.mutate(selectedBR.id)}
              onReject={(reason) => rejectMut.mutate({ id: selectedBR.id, reason })}
              onRevoke={() => revokeMut.mutate(selectedBR.id)}
              onToggleRejectMode={() => setRejectMode(r => !r)}
              onCancelReject={() => { setRejectMode(false); setRejectReason('') }}
              isApprovePending={approveMut.isPending}
              isRejectPending={rejectMut.isPending}
              isRevokePending={revokeMut.isPending}
            />

            {/* Tabs */}
            <div className="flex gap-1 border-b border-slate-200">
              {(['details', 'attachments', 'comments'] as const).map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${tab === t ? 'border-red-800 text-red-800' : 'border-transparent text-slate-500'}`}>
                  {t}
                </button>
              ))}
            </div>

            {tab === 'details' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ['Budget Type', selectedBR.budget_type],
                    ['Amount', formatAmount(Number(selectedBR.amount))],
                    ['Requested Date', formatDate(selectedBR.requested_date)],
                    ['Submitted By', selectedBR.submitted_by_name || '—'],
                    ['Submitted At', formatDateTime(selectedBR.created_at)],
                    ['Additional Approvers', (selectedBR.additional_approvers || []).length > 0 ? (selectedBR.additional_approvers || []).join(', ') : '—'],
                  ].map(([label, value]) => (
                    <div key={label as string} className="text-sm">
                      <span className="text-slate-500 text-xs block">{label}</span>
                      <span className="text-slate-900 font-medium">{value}</span>
                    </div>
                  ))}
                </div>

                {(selectedBR.project_ids?.length > 0) ? (
                  <div className="text-sm col-span-2">
                    <span className="text-slate-500 text-xs block mb-1">
                      {selectedBR.project_ids.length === 1 ? 'Linked Project' : 'Linked Projects'}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedBR.project_ids.map(p => (
                        <span key={p.id} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-50 text-red-800 border border-red-200 rounded-full">
                          {p.project_type}-{p.project_code} — {p.client_name}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : selectedBR.projects ? (
                  <div className="text-sm">
                    <span className="text-slate-500 text-xs block">Linked Project</span>
                    <span className="text-slate-900 font-medium">
                      {selectedBR.projects.project_type}-{selectedBR.projects.project_code} — {selectedBR.projects.client_name}
                    </span>
                  </div>
                ) : null}

                {selectedBR.clients && !(selectedBR.project_ids?.length > 0) && !selectedBR.projects && (
                  <div className="text-sm">
                    <span className="text-slate-500 text-xs block">Linked Contact</span>
                    <span className="text-slate-900 font-medium">
                      {selectedBR.clients.client_name}{selectedBR.clients.company ? ` — ${selectedBR.clients.company}` : ''}
                    </span>
                  </div>
                )}

                {selectedBR.disbursed_at && (
                  <div className="text-sm col-span-2 bg-teal-50 border border-teal-200 rounded-lg p-3">
                    <span className="text-teal-600 text-xs block font-medium mb-1">Disbursement Info</span>
                    <div className="flex flex-wrap gap-4">
                      <span className="text-xs text-slate-600">Date: <strong className="text-slate-800">{formatDateTime(selectedBR.disbursed_at)}</strong></span>
                      {selectedBR.disbursement_reference && (
                        <span className="text-xs text-slate-600">Ref: <strong className="text-slate-800">{selectedBR.disbursement_reference}</strong></span>
                      )}
                    </div>
                  </div>
                )}

                {selectedBR.notes && (
                  <div className="text-sm">
                    <span className="text-slate-500 text-xs block">Notes</span>
                    <p className="text-slate-700 whitespace-pre-wrap">{selectedBR.notes}</p>
                  </div>
                )}
              </div>
            )}

            {tab === 'attachments' && (
              <div className="space-y-5">
                {/* Photos */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Image size={14} /> Photos</h3>
                    {canUpload && (
                      <label className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 cursor-pointer">
                        <Upload size={12} /> {uploadingPhoto ? 'Uploading...' : 'Upload'}
                        <input ref={photoRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
                      </label>
                    )}
                  </div>
                  {photoError && <p className="text-xs text-red-600 mb-2">{photoError}</p>}
                  {(selectedBR.photos || []).length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">No photos uploaded</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {(selectedBR.photos || []).map((photo: DealAttachment) => {
                        const src = budgetRequestApi.getFileSrc(photo, selectedBR.id)
                        return (
                          <div key={photo.id} className="relative group rounded-lg overflow-hidden border border-slate-200 aspect-square bg-slate-100">
                            <a href={src} target="_blank" rel="noreferrer">
                              <img src={src} alt={photo.name} className="w-full h-full object-cover" />
                            </a>
                            {canUpload && (
                              <button
                                onClick={() => { if (confirm('Remove this photo?')) deletePhotoMut.mutate({ id: selectedBR.id, photoId: photo.id }) }}
                                className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              ><X size={10} /></button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Documents */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><FileText size={14} /> Documents</h3>
                    {canUpload && (
                      <label className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 cursor-pointer">
                        <Upload size={12} /> {uploadingDoc ? 'Uploading...' : 'Upload'}
                        <input ref={docRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*" className="hidden" onChange={handleDocUpload} disabled={uploadingDoc} />
                      </label>
                    )}
                  </div>
                  {docError && <p className="text-xs text-red-600 mb-2">{docError}</p>}
                  {(selectedBR.documents || []).length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">No documents uploaded</p>
                  ) : (
                    <div className="space-y-2">
                      {(selectedBR.documents || []).map((doc: DealAttachment) => {
                        const src = budgetRequestApi.getFileSrc(doc, selectedBR.id)
                        return (
                          <div key={doc.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <FileText size={16} className="text-red-800 shrink-0" />
                            <a href={src} target="_blank" rel="noreferrer" className="flex-1 text-sm text-slate-700 hover:text-red-800 truncate">{doc.name}</a>
                            <span className="text-xs text-slate-400">{doc.size ? `${(doc.size / 1024).toFixed(0)} KB` : ''}</span>
                            {canUpload && (
                              <button
                                onClick={() => { if (confirm('Remove this document?')) deleteDocMut.mutate({ id: selectedBR.id, docId: doc.id }) }}
                                className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                              ><X size={14} /></button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === 'comments' && (
              <CommentsSection entityType="budget_request" entityId={selectedBR.id} />
            )}
          </div>
        )}
      </SlideOver>
    </div>
  )
}
